// API service for communicating with Flask backend
// Always use production domain for API calls
const API_BASE_URL = 'https://fciveria.ge';

const API_URL = `${API_BASE_URL}/api`;

class ApiService {
  constructor() {
    this.baseUrl = API_URL;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Get auth token from localStorage for admin operations
    const storedUser = localStorage.getItem('fciveria_admin_user');
    const token = storedUser ? JSON.parse(storedUser).token : null;
    
    // Debug logging
    console.log('API Request Debug:', {
      url,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'No token',
      method: options.method || 'GET'
    });
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        // Enhanced error handling for 401
        if (response.status === 401) {
          console.error('Authentication failed. Possible causes:');
          console.error('1. Token expired (tokens expire after 1 hour)');
          console.error('2. User not logged in');
          console.error('3. Invalid token');
          console.error('4. User account deactivated');
          
          // Clear invalid token from localStorage
          localStorage.removeItem('fciveria_admin_user');
          
          // Trigger global logout by dispatching a custom event
          window.dispatchEvent(new CustomEvent('auth:logout', {
            detail: { reason: 'token_expired' }
          }));
          
          // Try to get error details from response
          let errorDetails = '';
          try {
            const errorData = await response.json();
            errorDetails = errorData.error || '';
          } catch (e) {
            errorDetails = 'Authentication failed';
          }
          
          throw new Error(`Authentication failed: ${errorDetails}`);
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // News API methods
  async getNews() {
    return this.request('/news');
  }

  async getPublishedNews() {
    return this.request('/news/published');
  }

  async getNewsById(id) {
    return this.request(`/news/${id}`);
  }

  async createNews(newsData) {
    return this.request('/news', {
      method: 'POST',
      body: JSON.stringify(newsData),
    });
  }

  async updateNews(id, newsData) {
    return this.request(`/news/${id}`, {
      method: 'PUT',
      body: JSON.stringify(newsData),
    });
  }

  async deleteNews(id) {
    return this.request(`/news/${id}`, {
      method: 'DELETE',
    });
  }

  // Match API methods
  async getMatches() {
    return this.request('/matches');
  }

  async getUpcomingMatch() {
    return this.request('/matches/upcoming');
  }

  async getPreviousMatch() {
    return this.request('/matches/previous');
  }

  async getMatchById(id) {
    return this.request(`/matches/${id}`);
  }

  async createMatch(matchData) {
    return this.request('/matches', {
      method: 'POST',
      body: JSON.stringify(matchData),
    });
  }

  async updateMatch(id, matchData) {
    return this.request(`/matches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(matchData),
    });
  }

  async deleteMatch(id) {
    return this.request(`/matches/${id}`, {
      method: 'DELETE',
    });
  }

  // Standings API methods
  async getStandings() {
    return this.request('/standings');
  }

  async getStandingById(id) {
    return this.request(`/standings/${id}`);
  }

  async createStanding(standingData) {
    return this.request('/standings', {
      method: 'POST',
      body: JSON.stringify(standingData),
    });
  }

  async updateStanding(id, standingData) {
    return this.request(`/standings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(standingData),
    });
  }

  async deleteStanding(id) {
    return this.request(`/standings/${id}`, {
      method: 'DELETE',
    });
  }

  // Health check
  async healthCheck() {
    return this.request('/health');
  }

  // Authentication helper methods
  isAuthenticated() {
    const storedUser = localStorage.getItem('fciveria_admin_user');
    if (!storedUser) return false;
    
    try {
      const user = JSON.parse(storedUser);
      return !!(user && user.token);
    } catch (e) {
      localStorage.removeItem('fciveria_admin_user');
      return false;
    }
  }

  getCurrentUser() {
    const storedUser = localStorage.getItem('fciveria_admin_user');
    if (!storedUser) return null;
    
    try {
      return JSON.parse(storedUser);
    } catch (e) {
      localStorage.removeItem('fciveria_admin_user');
      return null;
    }
  }

  // Verify token with backend
  async verifyToken() {
    try {
      const response = await this.request('/auth/verify');
      return response.valid;
    } catch (error) {
      console.error('Token verification failed:', error);
      return false;
    }
  }

  // Financial Documents API
  async getFinancialDocuments() {
    return this.request('/financial-documents');
  }

  async getAllFinancialDocuments() {
    return this.request('/financial-documents/all');
  }

  async getFinancialDocumentById(id) {
    return this.request(`/financial-documents/${id}`);
  }

  async createFinancialDocument(data) {
    return this.request('/financial-documents', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  async updateFinancialDocument(id, data) {
    return this.request(`/financial-documents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  async deleteFinancialDocument(id) {
    return this.request(`/financial-documents/${id}`, {
      method: 'DELETE'
    });
  }

  async downloadFinancialDocument(id) {
    const url = `${this.baseUrl}/financial-documents/${id}/download`;
    const storedUser = localStorage.getItem('fciveria_admin_user');
    const token = storedUser ? JSON.parse(storedUser).token : null;
    
    if (token) {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `financial-document-${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      } else {
        throw new Error('Download failed');
      }
    } else {
      throw new Error('Authentication required');
    }
  }
}

const apiService = new ApiService();
export default apiService;
